import React from 'react';
import PropTypes from 'prop-types';
import { ArrowLabel, Spacing, Text } from '@reservamos/elements';
import arrowLine from 'images/arrow-line.svg';
import TerminalNameWithLocationLink from 'ui/atoms/TerminalNameWithLocationLink';
import AnimationOverview from './AnimationOverview';

/**
 * Represents a trip schedule component.
 *
 * @param {Object} props - departureHour, arrivalHour, mainContent, mobileSize, departureTerminal, arrivalTerminal, showTerminals, hideMainContent, departureMapsLink, arrivalMapsLink.
 * @param {string} props.departureHour - The departure hour of the trip.
 * @param {string} props.arrivalHour - The arrival hour of the trip.
 * @param {string|array} props.mainContent - The main content of the trip.
 * @param {string} props.mobileSize - The mobile size of the component.
 * @param {string} props.departureTerminal - The departure terminal name.
 * @param {string} props.arrivalTerminal - The arrival terminal name.
 * @param {boolean} props.showTerminals - Whether to show terminal names.
 * @param {boolean} props.hideMainContent - When true, hides the main content and shows only the arrow line.
 * @param {string} props.departureMapsLink - Google Maps link for the departure terminal. When set (and showTerminals is true), the terminal renders as a location link instead of plain text.
 * @param {string} props.arrivalMapsLink - Google Maps link for the arrival terminal. When set (and showTerminals is true), the terminal renders as a location link instead of plain text.
 * @returns {JSX.Element} The rendered trip schedule component.
 */
const TripSchedule = ({
  departureHour,
  arrivalHour,
  mainContent,
  mobileSize,
  departureTerminal,
  arrivalTerminal,
  showTerminals,
  hideMainContent,
  departureMapsLink,
  arrivalMapsLink,
}) => {
  const contentElement =
    typeof mainContent === 'object' ? (
      <div className="animation-content">
        <AnimationOverview contentTypes={mainContent} />
      </div>
    ) : (
      <Text color="grayLight" size="XS" textAlign="center">
        {mainContent}
      </Text>
    );
  const mainElement = hideMainContent ? null : contentElement;

  const showDepartureTerminal = showTerminals && Boolean(departureTerminal);
  const showArrivalTerminal = showTerminals && Boolean(arrivalTerminal);

  // When a Google Maps link is provided, the terminal renders as a location link. Since the link
  // is a block element, it is stacked below the hour (it can't be nested inside the hour <Text>,
  // which is a <p>). Without a link the terminal keeps its original inline-under-the-hour render.
  const departureText =
    showDepartureTerminal && departureMapsLink ? (
      <Spacing vertical size="XS" alignItems="center">
        <Text mobileSize={mobileSize} size="XL" weight="semibold">
          {departureHour}
        </Text>
        <TerminalNameWithLocationLink
          terminalName={departureTerminal}
          mapsLink={departureMapsLink}
        />
      </Spacing>
    ) : (
      <Text mobileSize={mobileSize} size="XL" weight="semibold">
        {departureHour}
        {showDepartureTerminal && (
          <Text size="XS" weight="regular">
            {departureTerminal}
          </Text>
        )}
      </Text>
    );

  const arrivalText =
    showArrivalTerminal && arrivalMapsLink ? (
      <Spacing vertical size="XS" alignItems="center">
        <Text mobileSize={mobileSize} size="XL">
          {arrivalHour}
        </Text>
        <TerminalNameWithLocationLink terminalName={arrivalTerminal} mapsLink={arrivalMapsLink} />
      </Spacing>
    ) : (
      <Text mobileSize={mobileSize} size="XL">
        {arrivalHour}
        {showArrivalTerminal && (
          <Text size="XS" weight="regular">
            {arrivalTerminal}
          </Text>
        )}
      </Text>
    );

  if (!mainElement) {
    return (
      <Spacing size="S" alignItems="center" justifyContent="space-between" fullWidth>
        {departureText}
        <img src={arrowLine} alt="" />
        {arrivalText}
      </Spacing>
    );
  }

  return (
    <>
      {departureText}
      <ArrowLabel>{mainElement}</ArrowLabel>
      {arrivalText}
    </>
  );
};

TripSchedule.propTypes = {
  departureHour: PropTypes.string,
  arrivalHour: PropTypes.string,
  mainContent: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  mobileSize: PropTypes.string,
  departureTerminal: PropTypes.string,
  arrivalTerminal: PropTypes.string,
  showTerminals: PropTypes.bool,
  hideMainContent: PropTypes.bool,
  departureMapsLink: PropTypes.string,
  arrivalMapsLink: PropTypes.string,
};

TripSchedule.defaultProps = {
  showTerminals: false,
  mobileSize: 'M',
  departureTerminal: '',
  arrivalTerminal: '',
  hideMainContent: false,
  departureMapsLink: '',
  arrivalMapsLink: '',
};

export default TripSchedule;
